import React, { useState, useEffect } from 'react'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import moment from 'moment'
import { autoCompleteOff } from 'Helper/autoCompleteOff.js'
import { newInstance } from 'Helper/deepEqual'

export function defaultCompare(a, b) {
  // by default, treat undefined / null as the empty string
  a = a || ''
  b = b || ''
  return a === b ? 0 : a > b ? 1 : -1
}

export function FloatComparisonInput(props) {
  const filter = props.filter
  const operator =
    filter && filter.value && filter.value.operator
      ? filter.value.operator
      : '<'
  const value =
    filter && filter.value && filter.value.value ? filter.value.value : ''
  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <select
        style={{ width: '55px' }}
        value={operator}
        onChange={e => {
          if (e) e.persist() // need this because this is a syntheticEvent (see React docs)
          props.onChange({
            operator: e.target.value,
            value
          })
        }}
      >
        <option value="<">&lt;</option>
        <option value="<=">&le;</option>
        <option value="=">{'='}</option>
        <option value=">=">&ge;</option>
        <option value=">">&gt;</option>
      </select>
      <input
        className={'float-comparison-input ' + props.styleClassName} // removing inline style at some point, so I am making this styleClassName to use for styling
        style={{ ...props.style, flex: '1 1 -1' }}
        value={value}
        type={'text'}
        aria-label={'filter for ' + props.name}
        onChange={filter => {
          if (filter) filter.persist() // need this because this is a syntheticEvent (see React docs)
          props.onChange({
            operator,
            value: filter.target.value
          })
        }}
      />
    </div>
  )
}

export function XtractDateRangePicker(props) {
  const $ = window.jQuery

  const { ariaLabel, onChange, id, startDate, endDate, ranges } = props

  const [value, setValue] = useState()
  const [prevValue, setPrevValue] = useState($('#' + id).val(value))
  const [initialRange, setInitialRange] = useState()

  const parseDateRange = range => {
    const parseDateOperation = obj => {
      let value =
        typeof obj.value === 'object' && obj.value !== null
          ? parseDateOperation(obj.value)
          : moment()

      if (obj.operator !== undefined) {
        if (obj.units !== undefined) {
          if (obj.amount !== undefined) {
            value = value[obj.operator](obj.amount, obj.units)
          } else {
            value = value[obj.operator](obj.units)
          }
        }
      }
      return value
    }

    const start = parseDateOperation(range[0])
    const end = parseDateOperation(range[1])
    return [start, end]
  }

  // list of canned date ranges
  // format of each range is:
  //
  //  {
  //    name: name to display,
  //    range: array of [start, end] times
  //  }
  //
  //  each 'time' is computed using moment() library functions controlled by an object like:
  //
  //  {
  //    value:    base time
  //              - if not present, now() is used
  //              - could be literal date/time but that's a bit senseless
  //              - can be a 'time' (recursively)
  //    operator: one of [add, subtract, startOf, endOf]
  //    amount:   number of units to add or subtract
  //    units:    one of [year, month, day]
  //  }
  //
  //  so, the start of last month is:
  //
  //  {
  //   value: {
  //     operator: "subtract",
  //     amount: 1,
  //     units: "month"
  //   },
  //   operator: "startOf",
  //   units: "month"
  // }

  const propRanges = ranges || [
    {
      name: 'Today',
      range: [{}, {}]
    },
    {
      name: 'Yesterday',
      range: [
        {
          operator: 'subtract',
          amount: 1,
          units: 'day'
        },
        {
          operator: 'subtract',
          amount: 1,
          units: 'day'
        }
      ]
    },
    {
      name: 'Last 7 Days',
      range: [
        {
          operator: 'subtract',
          amount: 6,
          units: 'day'
        },
        {}
      ]
    },
    {
      name: 'Next 7 Days',
      range: [
        {},
        {
          operator: 'add',
          amount: 6,
          units: 'day'
        }
      ]
    },
    {
      name: 'Last 30 Days',
      range: [
        {
          operator: 'subtract',
          amount: 29,
          units: 'day'
        },
        {}
      ]
    },
    {
      name: 'Next 30 Days',
      range: [
        {},
        {
          operator: 'add',
          amount: 29,
          units: 'day'
        }
      ]
    },
    {
      name: 'Last Month',
      range: [
        {
          value: {
            operator: 'subtract',
            amount: 1,
            units: 'month'
          },
          operator: 'startOf',
          units: 'month'
        },
        {
          value: {
            operator: 'subtract',
            amount: 1,
            units: 'month'
          },
          operator: 'endOf',
          units: 'month'
        }
      ]
    },
    {
      name: 'This Month',
      range: [
        {
          operator: 'startOf',
          units: 'month'
        },
        {
          operator: 'endOf',
          units: 'month'
        }
      ]
    },
    {
      name: 'Next Month',
      range: [
        {
          value: {
            operator: 'add',
            amount: 1,
            units: 'month'
          },
          operator: 'startOf',
          units: 'month'
        },
        {
          value: {
            operator: 'add',
            amount: 1,
            units: 'month'
          },
          operator: 'endOf',
          units: 'month'
        }
      ]
    },
    {
      name: 'Last Year',
      range: [
        {
          value: {
            operator: 'subtract',
            amount: 1,
            units: 'year'
          },
          operator: 'startOf',
          units: 'year'
        },
        {
          value: {
            operator: 'subtract',
            amount: 1,
            units: 'year'
          },
          operator: 'endOf',
          units: 'year'
        }
      ]
    },
    {
      name: 'This Year',
      range: [
        {
          operator: 'startOf',
          units: 'year'
        },
        {
          operator: 'endOf',
          units: 'year'
        }
      ]
    }
  ]

  const parsedRanges = {}
  propRanges.forEach(range => {
    parsedRanges[range.name] = parseDateRange(range.range)
  })

  useEffect(() => {
    // if the initial range has a range label, use the computed (start, end)
    // otherwise use the given (start, end)

    const initialRange = newInstance(props.initialRange)
    let value = ''
    if (initialRange) {
      if (initialRange.chosenLabel) {
        const chosenRange = Object.keys(parsedRanges).find(
          range => range === initialRange.chosenLabel
        )

        if (chosenRange) {
          initialRange.min = moment(parsedRanges[chosenRange][0]).startOf('day').format('YYYY-MM-DD HH:mm:ss')
          initialRange.max = moment(parsedRanges[chosenRange][1]).endOf('day').format('YYYY-MM-DD HH:mm:ss')
          value = initialRange.min + ' to ' + initialRange.max
        }
      }

      if (!value) {
        value =
          initialRange.min && initialRange.max
            ? initialRange.min + ' to ' + initialRange.max
            : ''
      }
    }

    setValue(value)
    setInitialRange(initialRange)
  }, [])

  useEffect(() => {
    setValue(
      startDate && endDate
        ? moment(startDate).format('YYYY-MM-DD') + ' to ' + moment(endDate).format('YYYY-MM-DD')
        : ''
    )
  }, [startDate, endDate])

  // don't render until we have had a chance to initialize

  if (Object.keys(parsedRanges).length === 0) {
    return null
  }
  return (
    <DateRangePicker
      initialSettings={{
        alwaysShowCalendars: true,
        // Keep this false. When the picker has an initial range (e.g. a column
        // with a default "This Year" filter), autoUpdateInput: true makes
        // bootstrap-daterangepicker re-write its internal range back into the
        // text box on hide(). Since "Clear" (cancel) reverts that internal range
        // to the previous value, a later hide() would undo the clear and the
        // filter could never be removed. We drive the input value ourselves via
        // onApply / onCancel instead.
        autoUpdateInput: false,
        applyButtonClasses: 'btn-success',
        locale: {
          format: 'YYYY-MM-DD',
          separator: ' to ',
          cancelLabel: 'Clear',
          startDate: initialRange
            ? initialRange.min
            : null,
          endDate: initialRange
            ? initialRange.max
            : null
        },
        startDate: initialRange?.min || startDate,
        endDate: initialRange?.max || endDate,
        ranges: parsedRanges
      }}
      onApply={(e, picker) => {
        const value =
          picker.startDate.format('YYYY-MM-DD') +
          ' to ' +
          picker.endDate.format('YYYY-MM-DD')
        $('#' + id).val(value)
        setValue(value)
        onChange({
          min: picker.startDate.format('YYYY-MM-DD HH:mm:ss'),
          max: picker.endDate.format('YYYY-MM-DD HH:mm:ss'),
          chosenLabel: picker.chosenLabel
        })
      }}
      onCancel={e => {
        $('#' + id).val('')
        setValue('')
        onChange({})
      }}
    >
      <input
        id={id}
        type="text"
        className="form-control"
        defaultValue={value}
        aria-label={ariaLabel}
        {...autoCompleteOff}
        onChange={e => setPrevValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            if (prevValue === '') {
              e.preventDefault() // Ensure it is only this code that runs
              $('#' + id).val('')
              setValue('')
              onChange({})
            } else {
              const [start, end] = prevValue.split(/ *to */)
              const startDate = new moment(start)
              const endDate = new moment(end)
              setValue(prevValue)
              onChange({
                min: startDate.format('YYYY-MM-DD HH:mm:ss'),
                max: endDate.format('YYYY-MM-DD HH:mm:ss'),
                chosenLabel: 'Custom Range'
              })
            }
          }
        }}
      />
    </DateRangePicker>
  )
}

export function tryParseInt(str, defaultValue) {
  let retValue = defaultValue
  if (str !== null) {
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseInt(str)
      }
    }
  }
  return retValue
}

export function tryParseFloat(str, defaultValue) {
  let retValue = defaultValue
  if (str !== null) {
    if (str.length > 0) {
      if (!isNaN(str)) {
        retValue = parseFloat(str)
      }
    }
  }
  return retValue
}

export function filterInteger(filter, row) {
  if (filter.value === '') {
    return true
  }

  const intFilter = tryParseInt(filter.value, null)
  const val = row[filter.id]
  const intVal =
    typeof val === 'number'
      ? Math.floor(val)
      : tryParseInt(row[filter.id], null)

  return intFilter !== null && intVal === intFilter
}

export function filterFloat(filter, row) {
  if (filter.value === '') {
    return true
  }

  const floatFilter = tryParseFloat(filter.value, null)
  const val = row[filter.id]
  const intVal =
    typeof val === 'number' ? val : tryParseFloat(row[filter.id], null)

  return floatFilter !== null && intVal.toFixed(3) === floatFilter.toFixed(3)
}

export function FilterFloatInput(style) {
  return function({ filter, onChange }) {
    return (
      <input
        style={{ ...style }}
        type={'text'}
        aria-label={'filter for ' + (filter ? filter.id : '')}
        onChange={filter => {
          if (filter) filter.persist() // need this because this is a syntheticEvent (see React docs)
          onChange(filter.target.value)
        }}
      />
    )
  }
}

export function FilterDilutionInput(props) {
  return (
    <div className="filter-dilution" style={{ display: 'flex' }}>
      <div style={{ width: '20px', margin: 'auto' }}>1:</div>
      <input
        style={{ width: 'calc(100% - 20px)' }}
        type="number"
        defaultValue={props.filter}
        min={0}
        aria-label={
          'filter for ' + (props.name || '')
        }
        onChange={filter => {
          if (filter) filter.persist() // need this because this is a syntheticEvent (see React docs)
          props.onChange(filter.target.value)
        }}
      />
    </div>
  )
}

export function filterFloatRange(filter, row) {
  if (filter.value.value === '') {
    return true
  }

  const floatFilter = tryParseFloat(filter.value.value, null)
  const operator = filter.value.operator
  const val = row[filter.id]
  const floatVal =
    typeof val === 'number' ? val : tryParseFloat(row[filter.id], null)

  if (floatFilter === null) {
    return false
  }
  switch (operator) {
    case '<':
      return floatVal < floatFilter
    case '<=':
      return floatVal <= floatFilter
    case '=':
      return floatVal.toFixed(3) === floatFilter.toFixed(3)
    case '>=':
      return floatVal >= floatFilter
    case '>':
      return floatVal > floatFilter
    default:
      return false
  }
}

export function FilterFloatRangeInput(filterProps) {
  return function({ filter, onChange }) {
    return (
      <FloatComparisonInput
        name={filter ? filter.id : ''}
        style={{ ...filterProps.style }}
        filter={{ ...filterProps.filter }}
        onChange={props => {
          onChange(props)
          filterProps.onChangeValue(props)
        }}
      />
    )
  }
}

export function filterStringNoCase(filter, row) {
  const included =
    typeof filter.value === 'string' &&
    typeof row[filter.id] === 'string' &&
    row[filter.id].toLowerCase().startsWith(filter.value.toLowerCase())
  return included
}

export function filterContains(filter, row) {
  // eslint-disable-next-line eqeqeq
  return (
    row[filter.id] == filter.value ||
    (row[filter.id] &&
      row[filter.id].toLowerCase().includes(filter.value.toLowerCase()))
  )
}

export function filterID(filter, row) {
  const included = !!filter.value.find(fltr => {
    return (
      ((row[filter.id] === undefined || row[filter.id] === '') &&
        fltr[filter.id] === 0) ||
      row[filter.id] === fltr[filter.id]
    )
  })
  return included
}

export function filterDateComparison(filter, row) {
  if (filter.value.value === '') {
    return true
  }
  const rowValue = row[filter.id]
  if (rowValue === undefined || rowValue === '') {
    return true
  }
  const filterDate = moment(filter.value.value)
  const rowDate = moment(rowValue)
  const daysDifference = rowDate.diff(filterDate, 'days')
  switch (filter.value.operator) {
    case '<':
      return daysDifference < 0
    case '<=':
      return daysDifference <= 0
    case '=':
      return daysDifference === 0
    case '>':
      return daysDifference > 0
    case '>=':
      return daysDifference >= 0
    default:
      return false
  }
}

export function dbFilterLastCommaFirst(lastnameKey, firstnameKey, needle) {
  // pull last and first names apart, handle them individually
  const [lastname, firstname] = needle.split(',')
  if (firstname !== undefined) {
    const lastNameValue = []
    lastNameValue[lastnameKey] = '%' + lastname.trim() + '%'
    const firstNameValue = []
    firstNameValue[firstnameKey] = '%' + firstname.trim() + '%'
    return {
      and: [
        { like: Object.assign({}, lastNameValue) },
        { like: Object.assign({}, firstNameValue) }
      ]
    }
  } else {
    const name = lastname
    const lastNameValue = []
    lastNameValue[lastnameKey] = '%' + name.trim() + '%'
    const firstNameValue = []
    firstNameValue[firstnameKey] = '%' + name.trim() + '%'
    return {
      or: [
        { like: Object.assign({}, lastNameValue) },
        { like: Object.assign({}, firstNameValue) }
      ]
    }
  }
}

export function convertFiltersToJSON(filters, listConfig) {
  if (!filters || filters.length === 0) {
    return {}
  }

  const operatorLookup = {
    '<': 'less',
    '<=': 'less_or_equal',
    '=': 'equal',
    '>=': 'greater_or_equal',
    '>': 'greater'
  }
  const filterArray = filters
    .map(filter => {
      let dbFilter = ''
      if (listConfig) {
        // get the database filter from the column definitions
        const column = listConfig.find(column => column.id === filter.id)
        if (column && column.dbFilter) {
          dbFilter = column.dbFilter
        }
      } else {
        throw new Error('Deprecated filter usage - update callers parameter list to match v8')
      }
      if (Array.isArray(filter.value)) {
        // if it is an array then use the SQL 'IN' operator

        const fieldNameAndValueList = {}
        const fieldName = filter.id
        let containsNull = false

        fieldNameAndValueList[filter.id] = filter.value
          .map(value => {
            containsNull |= value[fieldName] === null
            return value[fieldName]
          })
          .filter(value => value !== null)

        const inFilter = { in: fieldNameAndValueList }

        if (containsNull) {
          return { or: [inFilter, { is_null: fieldName }] }
        } else {
          return inFilter
        }
      } else {
        if (typeof filter.value === 'object') {
          const value = filter.value
          if (value.min !== undefined && value.max !== undefined) {
            // it's a BETWEEN
            const targetValue = []
            if (dbFilter === 'dateOnly') {
              targetValue[filter.id] = [
                value.min,
                value.max
              ]
            } else {
              targetValue[filter.id] = [value.min, value.max]
            }
            return Object.assign(
              {},
              { between: Object.assign({}, targetValue) }
            )
          } else {
            // if it's an unknown condition or if the value to compare against is empty, ignore it

            if (
              typeof operatorLookup[value.operator] === 'undefined' ||
              value.value === ''
            ) {
              return null
            } else {
              const targetValue = []
              targetValue[filter.id] = value.value
              const condition = []
              condition[operatorLookup[value.operator]] = Object.assign(
                {},
                targetValue
              )
              return Object.assign({}, condition)
            }
          }
        } else {
          if (typeof dbFilter === 'function') {
            return dbFilter(filter)
          }
          const targetValue = []
          if (dbFilter === 'boolean') {
            switch (filter.value.toLowerCase()) {
              case 'y':
              case 'yes':
              case 't':
              case 'true': {
                targetValue[filter.id] = 1
                break
              }
              case 'n':
              case 'no':
              case 'f':
              case 'false': {
                targetValue[filter.id] = 0
                break
              }
              default:
                targetValue[filter.id] = null
            }
            if (targetValue[filter.id] === null) {
              targetValue[filter.id] = 0
              const isFalse = {
                equal: Object.assign({}, targetValue)
              }
              targetValue[filter.id] = 1
              const isTrue = {
                equal: Object.assign({}, targetValue)
              }
              return { or: [isFalse, isTrue] }
            } else {
              return {
                equal: Object.assign({}, targetValue)
              }
            }
          } else {
            if (dbFilter === 'checkForNull') {
              switch (filter.value) {
                case 'isNotNull': {
                  return { is_not_null: filter.id }
                }
                case 'isNull': {
                  return { is_null: filter.id }
                }
                default: {
                  return null
                }
              }
            } else {
              switch (dbFilter) {
                case 'startsWith': {
                  targetValue[filter.id] = filter.value + '%'
                  break
                }
                case 'equal': {
                  targetValue[filter.id] = filter.value
                  break
                }
                default: {
                  // 'contains'
                  targetValue[filter.id] = '%' + filter.value + '%'
                }
              }
              return {
                like: Object.assign({}, targetValue)
              }
            }
          }
        }
      }
    })
    .filter(filter => {
      // filter out empty conditions
      return !!filter
    })

  // AND together all the conditions

  return filterArray.length === 0 ? null : { and: filterArray }
}

export function getXtractTableState(userStateKey, patientId) {
  const sessionUserState = sessionStorage.getItem(userStateKey)
  const localUserState = localStorage.getItem(userStateKey)

  let localJson
  let sessionJson
  try {
    localJson = JSON.parse(localUserState)
  } catch (e) {
    localJson = {}
  }

  // if we have changed patients, then don't restore the settings
  // if we have changed patients, then don't restore the settings
  try {
    sessionJson = JSON.parse(sessionUserState)
    if (patientId && sessionJson.patientId === patientId) {
      // same patient so we can restore settings but remove the id from
      // the settings now that the check has passed
      delete sessionJson.patientId
    } else if(patientId || sessionJson.patientId) {
      // we had at least one patientId and the values didn't match so we need
      // to clear the data.
      sessionJson = {}
    } else {
      // this happens when the session data had no patientId and the
      // function was called with no patient id which means there is no patient
      // sensitive data to clear and we can leave the session data intact.
    }
  } catch (e) {
    sessionJson = {}
  }

  return {
    ...localJson,
    ...sessionJson
  }
}
export function saveXtractTableState(userStateKey, state, patientId) {
  if (userStateKey) {
    const {
      showFilters,
      sorted,
      pageSize,
      columnSizing,
      filtered,
      selectedColumns
    } = state

    const sessionUserState = {
      patientId,
      showFilters,
      sorted,
      pageSize,
      columnSizing,
      filtered: showFilters ? filtered : [],
      selectedColumns
    }

    const localUserState = {
      selectedColumns
    }

    sessionStorage.setItem(userStateKey, JSON.stringify(sessionUserState))
    localStorage.setItem(userStateKey, JSON.stringify(localUserState))
  }
}

export function convertDotNotationArrayToObject(idArray) {
  // convert an array of dot-notation elements into an object

  const objectify = (list, obj) => {
    if (list.length === 0) {
      return null
    } else {
      const first = list.shift()
      if (obj[first]) {
        obj[first] = {
          ...obj[first],
          ...newInstance(objectify(list, obj[first]))
        }
      } else {
        obj[first] = newInstance(objectify(list, {}))
      }
    }
    return obj
  }

  const shape = idArray.reduce((acc, id) => {
    const parts = id.split('.')
    return {
      ...acc,
      ...objectify(parts, acc)
    }
  }, {})

  return shape
}

export const columnNameAsString = (column) => {
  let label = typeof column.header === 'function' ? column.header() : column.header
  if (column.headerName) {
    if (typeof column.headerName === 'function') {
      label = column.headerName()
    } else {
      label = column.headerName
    }
  }
  return label
}

export const TinyColumnChooser = (props) => {
  const { visible = {}, columns = [], onChange } = props

  const options =
    columns
      .filter(column => column.showable === undefined || column.showable)
      .map(column => { return { id: column.id, name: columnNameAsString(column) } })

  function countSelected() {
    return Object.values(visible).filter(el => el).length
  }

  function allSelected() {
    return countSelected() === options.length
  }

  function select(e) {
    const newVisible = { ...visible }
    const value = e.target.value

    if (value === 'all') {
      if (!allSelected()) {
        options.forEach((option, i) => {
          newVisible[option.id] = true
        })
      }
    } else {
      // don't deselect the last one otherwise all columns are gone and there's no way to select any
      if (countSelected() > 1 || !visible[value]) {
        newVisible[value] = !visible[value]
      }
    }

    onChange?.(newVisible)
  }

  return (
    <select className="tiny-column-chooser" value="" onChange={(e) => { select(e) }}>
      <option value="" disabled="" hidden="">⋮</option>
      {
        props.includeSelectAll
          ? <option
              key={'all'}
              value={'all'}
              className={allSelected() ? 'all selected' : 'all'}>{'Select all'}
            </option>
          : null
      }
      {
        options.map((option) => {
          return (
            <option
              key={option.id}
              value={option.id}
              className={visible[option.id] ? 'selected' : ''}>{option.name}
            </option>
          )
        })
      }
    </select>
  )
}
